// Dynamic city landing page — /bilforhandler-<citySlug>.
// Pulls content from `city_landing_pages` (published only for the public),
// builds rich SEO meta + JSON-LD (BreadcrumbList, AutoDealer with areaServed,
// FAQPage), and falls back to the city seed list if a page hasn't been
// published yet so the URL still renders something useful.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, MapPin, Phone, Mail, Car as CarIcon, ChevronRight } from "lucide-react";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { supabase } from "@/integrations/supabase/client";
import { buildSeoMeta, clampDescription } from "@/lib/seo";
import { buildBreadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { buildAutoDealerJsonLd } from "@/lib/jsonld";
import { CITY_SEEDS, findNeighborCities } from "@/lib/cities";
import { CONTACT } from "@/config/contact";
import { CarCard } from "@/components/CarCard";

interface UspBlock {
  title: string;
  body: string;
}
interface FaqBlock {
  question: string;
  answer: string;
}
interface SectionBlock {
  heading: string;
  body: string;
}

interface CityLandingData {
  city_slug: string;
  city_name: string;
  intro: string | null;
  usps: UspBlock[];
  faqs: FaqBlock[];
  body_sections: SectionBlock[];
  distance_km: number | null;
  hero_image: string | null;
  seo_title: string | null;
  seo_description: string | null;
  status: string;
  published_at: string | null;
}

const loadInput = z.object({ citySlug: z.string().min(1).max(60).regex(/^[a-z0-9-]+$/) });

const loadCityPage = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => loadInput.parse(input))
  .handler(async ({ data }) => {
    // Only show published pages publicly — admins can preview via /admin.
    const { data: row } = await supabaseAdmin
      .from("city_landing_pages")
      .select(
        "city_slug, city_name, intro, usps, faqs, body_sections, distance_km, hero_image, seo_title, seo_description, status, published_at",
      )
      .eq("city_slug", data.citySlug)
      .eq("status", "published")
      .maybeSingle();

    if (row) return row as unknown as CityLandingData;

    // Fallback: if seed exists, return a minimal placeholder so URL still works.
    const seed = CITY_SEEDS.find((c) => c.slug === data.citySlug);
    if (!seed) return null;
    return {
      city_slug: seed.slug,
      city_name: seed.name,
      intro: null,
      usps: [],
      faqs: [],
      body_sections: [],
      distance_km: seed.distanceKm,
      hero_image: null,
      seo_title: null,
      seo_description: null,
      status: "draft",
      published_at: null,
    } as CityLandingData;
  });

export const Route = createFileRoute("/bilforhandler-{$citySlug}")({
  loader: async ({ params }) => {
    const page = await loadCityPage({ data: { citySlug: params.citySlug } });
    if (!page) throw notFound();
    return { page };
  },
  head: ({ loaderData }) => {
    const page = loaderData?.page;
    if (!page) {
      return buildSeoMeta({
        title: "Bilforhandler · AutoXChange",
        description: "Lokal bilforhandler i Trekantområdet — håndplukkede brugte biler.",
        path: "/",
      });
    }
    const title =
      page.seo_title ||
      `Bilforhandler ${page.city_name} · Brugte biler hos AutoXChange`;
    const description =
      page.seo_description ||
      clampDescription(
        page.intro ||
          `Leder du efter en bilforhandler i ${page.city_name}? AutoXChange ligger ${page.distance_km ?? ""} km væk i Børkop med håndplukkede brugte biler og fair priser.`,
      );

    const seo = buildSeoMeta({
      title,
      description,
      path: `/bilforhandler-${page.city_slug}`,
      image: page.hero_image || undefined,
      imageAlt: `Bilforhandler ${page.city_name} — AutoXChange`,
      geo: {
        region: "DK-83",
        placename: page.city_name,
        latitude: 55.6379,
        longitude: 9.6431,
      },
    });

    const breadcrumbs = buildBreadcrumbJsonLd([
      { name: "Forside", path: "" },
      { name: `Bilforhandler ${page.city_name}`, path: `bilforhandler-${page.city_slug}` },
    ]);

    const dealerWithArea = JSON.parse(buildAutoDealerJsonLd());
    dealerWithArea.areaServed = [
      { "@type": "City", name: page.city_name },
      { "@type": "Country", name: "Denmark" },
    ];

    const scripts: Array<{ type: string; children: string }> = [
      { type: "application/ld+json", children: breadcrumbs },
      { type: "application/ld+json", children: JSON.stringify(dealerWithArea) },
    ];

    if (page.faqs.length > 0) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: page.faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }),
      });
    }

    return { ...seo, scripts };
  },
  component: CityLandingPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="text-3xl font-bold text-foreground">Side ikke fundet</h1>
      <p className="mt-4 text-muted-foreground">
        Den by-side du leder efter findes ikke endnu.
      </p>
      <Link to="/" className="mt-8 inline-block text-accent hover:underline">
        Tilbage til forsiden
      </Link>
    </div>
  ),
});

function CityLandingPage() {
  const { page } = Route.useLoaderData();

  // Show a few cars on the page to keep CTAs concrete.
  const { data: cars } = useQuery({
    queryKey: ["city-landing-cars"],
    queryFn: async () => {
      const { data } = await supabase
        .from("cars")
        .select(
          "id, brand, model, variant, year, mileage, price, fuel_type, transmission, color, body_type, doors, seats, engine_size, horsepower, description, equipment, images, status, is_new, is_popular, is_leasing, leasing_price, is_demo, first_registration, seo_title, seo_description, seo_slug, created_at",
        )
        .eq("status", "active")
        .eq("is_demo", false)
        .order("created_at", { ascending: false })
        .limit(4);
      return data || [];
    },
  });

  const distance = page.distance_km ?? null;
  const intro =
    page.intro ||
    `Hos AutoXChange i Børkop ligger vi kun ${distance ?? "kort afstand"} km fra ${page.city_name}. Vi er en uafhængig bilforhandler med håndplukkede brugte biler — fair priser, fuld gennemsigtighed og ingen skjulte gebyrer.`;

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-dark-blue py-20 md:py-28">
        {page.hero_image ? (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: `url(${page.hero_image})` }}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-r from-dark-blue via-dark-blue/95 to-dark-blue/70" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <nav aria-label="Brødkrumme" className="flex items-center gap-2 text-xs text-white/70">
            <Link to="/" className="hover:text-white/70">Forside</Link>
            <ChevronRight size={12} />
            <span className="text-white/70">Bilforhandler {page.city_name}</span>
          </nav>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-badge-bg px-4 py-1.5 text-sm font-medium text-brand-badge-text">
            <MapPin size={14} />
            {distance ? `${distance} km fra ${page.city_name}` : `Tæt på ${page.city_name}`}
          </div>
          <h1 className="mt-6 max-w-3xl text-4xl font-bold leading-tight tracking-tight text-white md:text-6xl">
            Bilforhandler i <span className="text-accent">{page.city_name}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/60">{intro}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/biler-til-salg"
              className="inline-flex items-center gap-2 rounded-lg bg-accent px-8 py-4 text-sm font-semibold text-accent-foreground transition-all hover:brightness-110"
            >
              Se aktuelle biler <ArrowRight size={16} />
            </Link>
            <a
              href={CONTACT.phoneHref}
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-white/10"
            >
              <Phone size={14} /> Ring {CONTACT.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* USPs */}
      {page.usps.length > 0 && (
        <section className="bg-background py-20">
          <div className="mx-auto max-w-7xl px-6">
            <div className="grid gap-8 md:grid-cols-3">
              {page.usps.map((usp: UspBlock) => (
                <div
                  key={usp.title}
                  className="rounded-2xl border border-border bg-card p-8 transition-all hover:border-accent/30 hover:shadow-lg"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent">
                    <CarIcon size={20} />
                  </div>
                  <h3 className="mt-6 text-lg font-bold text-foreground">{usp.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{usp.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Body sections */}
      {page.body_sections.length > 0 && (
        <section className="bg-muted py-20">
          <div className="mx-auto max-w-3xl px-6">
            {page.body_sections.map((section: SectionBlock) => (
              <div key={section.heading} className="mb-12 last:mb-0">
                <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                  {section.heading}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground whitespace-pre-line">
                  {section.body}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Featured cars */}
      {cars && cars.length > 0 && (
        <section className="bg-background py-20">
          <div className="mx-auto max-w-7xl px-6">
            <div className="flex items-end justify-between">
              <div>
                <span className="text-sm font-semibold uppercase tracking-widest text-accent">
                  Lige nu
                </span>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground">
                  Brugte biler du kan se i dag
                </h2>
              </div>
              <Link
                to="/biler-til-salg"
                className="hidden items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:brightness-125 md:inline-flex"
              >
                Se alle biler <ChevronRight size={14} />
              </Link>
            </div>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {cars.map((car) => (
                <CarCard key={car.id} {...car} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {page.faqs.length > 0 && (
        <section className="bg-muted py-20">
          <div className="mx-auto max-w-3xl px-6">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              FAQ
            </span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground">
              Spørgsmål fra kunder i {page.city_name}
            </h2>
            <div className="mt-10 space-y-4">
              {page.faqs.map((faq: FaqBlock) => (
                <details
                  key={faq.question}
                  className="group rounded-xl border border-border bg-card p-6 transition-all hover:border-accent/30"
                >
                  <summary className="cursor-pointer list-none text-base font-semibold text-foreground">
                    <span className="flex items-center justify-between gap-4">
                      {faq.question}
                      <ChevronRight
                        size={16}
                        className="shrink-0 text-accent transition-transform group-open:rotate-90"
                      />
                    </span>
                  </summary>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground whitespace-pre-line">
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Neighbour cities — internal linking silo: each city page links to its
          3 geographically nearest neighbours so crawlers (and visitors) can
          hop between related local landing pages without going via the home page. */}
      {(() => {
        const neighbours = findNeighborCities(page.city_slug, 3);
        if (neighbours.length === 0) return null;
        return (
          <section className="bg-background py-20">
            <div className="mx-auto max-w-5xl px-6">
              <span className="text-sm font-semibold uppercase tracking-widest text-accent">
                Bilforhandler i nabobyer
              </span>
              <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                Også i nærheden af {page.city_name}
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                Vi betjener hele Trekantområdet og Sønderjylland. Bor du tættere på en af disse byer, er turen til Børkop stadig kort.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {neighbours.map((n) => (
                  <Link
                    key={n.slug}
                    to="/bilforhandler-{$citySlug}"
                    params={{ citySlug: n.slug }}
                    className="group flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-md"
                  >
                    <div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <MapPin size={12} className="text-accent" />
                        {n.distanceKm} km fra Børkop
                      </div>
                      <div className="mt-1 text-base font-semibold text-foreground group-hover:text-accent">
                        Bilforhandler {n.name}
                      </div>
                    </div>
                    <ArrowRight
                      size={16}
                      className="shrink-0 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-accent"
                    />
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* CTA */}
      <section className="relative overflow-hidden bg-dark-blue py-20">
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
            Klar til at finde din næste bil?
          </h2>
          <p className="mt-4 text-lg text-white/60">
            Kig forbi pladsen i Børkop — eller skriv til os, så finder vi den
            rigtige bil til dig i {page.city_name}.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href={CONTACT.phoneHref}
              className="inline-flex items-center gap-2 rounded-lg bg-accent px-8 py-4 text-sm font-semibold text-accent-foreground transition-all hover:brightness-110"
            >
              <Phone size={14} /> Ring {CONTACT.phoneDisplay}
            </a>
            <a
              href={CONTACT.emailHref}
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-white/10"
            >
              <Mail size={14} /> Skriv en mail
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
